

import React from 'react';
import { useStore } from '../store/useStore';
import { Check, X, Crown, Building2, User, Rocket } from 'lucide-react';
import { PLAN_LIMITS, PlanTier } from '../types';

const PricingModal: React.FC = () => {
  const { isPricingOpen, setPricingOpen, userProfile, upgradeSubscription, allowance } = useStore();

  if (!isPricingOpen) return null;
  
  // Safe plan lookup
  const currentPlan: PlanTier = (userProfile?.plan && PLAN_LIMITS[userProfile.plan]) ? userProfile.plan : 'free';

  const renderPlanCard = (tierKey: PlanTier, icon: React.ReactNode, recommended = false) => {
      const info = PLAN_LIMITS[tierKey];
      const isCurrent = currentPlan === tierKey;
      
      return (
          <div className={`
            relative p-6 rounded-2xl border flex flex-col h-full transition-all
            ${recommended ? 'bg-indigo-500/5 border-indigo-500/30 ring-1 ring-indigo-500/30' : 'bg-zinc-900 border-white/5 hover:border-white/20'}
            ${isCurrent ? 'border-emerald-500/30 bg-emerald-500/5' : ''}
          `}>
              {recommended && !isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-full shadow-[0_0_15px_rgba(99,102,241,0.5)] whitespace-nowrap w-auto">
                      Best Value
                  </div>
              )}
              {isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-full shadow-[0_0_15px_rgba(16,185,129,0.5)] whitespace-nowrap w-auto">
                      Current Plan
                  </div>
              )}
              
              <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 rounded-lg ${recommended ? 'bg-indigo-500/20 text-indigo-400' : 'bg-zinc-800 text-zinc-400'}`}>
                      {icon}
                  </div>
                  <div>
                      <h3 className="text-lg font-semibold text-white leading-none">{info.label}</h3>
                      <div className="text-xs text-zinc-500 mt-1">
                          {tierKey === 'starter' ? 'Side Projects' : tierKey === 'pro' ? 'Founders' : tierKey === 'executive' ? 'Power Users' : 'Hobbyist'}
                      </div>
                  </div>
              </div>

              <div className="mb-6">
                  <div className="text-3xl font-bold text-white flex items-end gap-1">
                      ${info.price}<span className="text-sm font-normal text-zinc-500 mb-1">{info.price > 0 ? '/mo' : ''}</span>
                  </div>
              </div>

              <div className="space-y-3 mb-8 flex-1">
                  <div className="flex items-center gap-3 text-sm text-zinc-300">
                      <Check size={16} className={info.models.includes('pro') ? "text-indigo-400" : "text-zinc-600"} />
                      {info.models.includes('pro') ? 'Advanced AI (Gemini Pro)' : 'Basic AI (Gemini Flash)'}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-zinc-300">
                      <Check size={16} className="text-indigo-400" />
                      {(info.credits / 1000).toLocaleString()}k Credits
                  </div>
                  <div className="flex items-center gap-3 text-sm text-zinc-300">
                      <Check size={16} className={info.privateIdeas ? "text-indigo-400" : "text-zinc-600"} />
                      {info.privateIdeas ? 'Private Ideas' : 'Public Ideas Only'}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-zinc-300">
                      <Check size={16} className={info.imageGeneration ? "text-indigo-400" : "text-zinc-600"} />
                      {info.imageGeneration ? 'Image Generation' : 'No Images'}
                  </div>
                  {info.prioritySupport && (
                      <div className="flex items-center gap-3 text-sm text-zinc-300">
                          <Check size={16} className="text-indigo-400" />
                          Priority Support
                      </div>
                  )}
              </div>

              <button
                onClick={() => !isCurrent && upgradeSubscription(tierKey)}
                disabled={isCurrent}
                className={`
                    w-full py-3 rounded-xl text-sm font-semibold transition-all
                    ${isCurrent 
                        ? 'bg-white/5 text-zinc-500 cursor-default' 
                        : recommended 
                            ? 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-lg shadow-indigo-500/25' 
                            : 'bg-white text-black hover:bg-zinc-200'}
                `}
              >
                  {isCurrent ? 'Active' : 'Upgrade'}
              </button>
          </div>
      );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={() => setPricingOpen(false)} />
        <div className="relative w-full max-w-6xl bg-black border border-white/10 rounded-3xl p-8 shadow-2xl overflow-y-auto max-h-[90vh]">
            <button onClick={() => setPricingOpen(false)} className="absolute top-6 right-6 p-2 text-zinc-500 hover:text-white transition-colors">
                <X size={24} />
            </button>

            <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-white mb-4">Choose your growth engine</h2>
                <p className="text-zinc-400 mb-8">Unlock advanced AI models, privacy, and asset generation.</p>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
                {renderPlanCard('free', <User size={20} />)}
                {renderPlanCard('starter', <Rocket size={20} />)}
                {renderPlanCard('pro', <Crown size={20} />, true)}
                {renderPlanCard('executive', <Building2 size={20} />)}
            </div>

            <div className="mt-12 p-6 bg-zinc-900/50 rounded-2xl border border-white/5 flex items-center justify-between">
                <div>
                    <h4 className="text-sm font-semibold text-white mb-1">Current Usage (Monthly)</h4>
                    <p className="text-xs text-zinc-500">
                        Flash = 1 Credit / Token. Pro = 10 Credits / Token.
                    </p>
                </div>
                <div className="flex gap-8 text-right">
                    <div>
                        <div className="text-2xl font-bold text-white">
                            {userProfile?.ideasCreated || 0}
                            <span className="text-sm font-normal text-zinc-500"> / {PLAN_LIMITS[currentPlan].credits === 10000 ? '3' : '∞'}</span>
                        </div>
                        <div className="text-xs text-zinc-500 uppercase tracking-wider">Ideas</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-white">
                            {allowance ? allowance.used.toLocaleString() : (userProfile?.tokensUsed.toLocaleString() || 0)}
                            <span className="text-sm font-normal text-zinc-500"> / {PLAN_LIMITS[currentPlan].credits.toLocaleString()}</span>
                        </div>
                        <div className="text-xs text-zinc-500 uppercase tracking-wider">Credits</div>
                    </div>
                </div>
            </div>
            
             <div className="mt-4 flex justify-center">
                 <button onClick={() => alert("Redirecting to billing portal...")} className="text-xs text-zinc-500 hover:text-white underline">
                    Manage Subscription & Billing
                 </button>
            </div>
        </div>
    </div>
  );
};

export default PricingModal;